import React, { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useProviderRequestSchema } from "@features/ProviderRequestModal/useProviderRequestSchema";
import { Button } from "@components/Button/Button";
import { FileUploadButton } from "@components/FileUploadButton/FileUploadButton";
import {
  AddDocumentIcon,
  DocumentIcon,
  MagnifyingGlassIcon,
} from "@components/icons";
import { FormAutocomplete } from "@components/inputs/form/FormAutocomplete/FormAutocomplete";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { FormTextarea } from "@components/inputs/form/FormTextarea/FormTextarea";
import { Modal } from "@components/Modal/Modal";
import { Notification } from "@components/Notification/Notification";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useOverviewQuery } from "@services/overview/queries";
import { type IOverviewJointBillService } from "@services/overview/types";
import { useAddRequestedServiceProviderMutation } from "@services/serviceProviders/mutations";
import { useRequestedServiceProvidersQuery } from "@services/serviceProviders/queries";
import { convertFileToDataUrl, isNullOrUndefined } from "@utils/common";
import { sanitizeHtml } from "@utils/html";

interface IFormData {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  requestedServiceProviderName: { value: number; label: string };
  customerCode: string;
  note: string;
  file?: File;
}

export const ProviderRequestModal: React.FC<IUseModalProps> = (props) => {
  const { t } = useTranslation();
  const modal = useModal(props);
  const { data: overview } = useOverviewQuery();
  const jointBillServiceIdOptions = (overview?.jointBillServices ?? []).map(
    (jbs) => ({
      label: jbs.displayName ?? t("common.defaultAddress"),
      value: jbs.jointBillServiceId,
    }),
  );
  const schema = useProviderRequestSchema();

  const { handleSubmit, control, setError, setValue } = useForm<IFormData>({
    defaultValues: {
      jointBillServiceId: overview?.jointBillServices?.[0]?.jointBillServiceId,
      requestedServiceProviderName: { label: "" },
    },
    resolver: zodResolver(schema),
  });

  const { setApiErrors } = useApiErrorUtils(setError);

  const { data: serviceProviders, isLoading: isServiceProvidersLoading } =
    useRequestedServiceProvidersQuery();

  const serviceProviderOptions = useMemo(
    () =>
      serviceProviders?.map((provider) => ({
        value: provider.requestedServiceProviderId,
        label: provider.requestedServiceProviderName,
      })),
    [serviceProviders],
  );

  const { mutateAsync: addRequestedServiceProvider, isPending } =
    useAddRequestedServiceProviderMutation();

  const [file] = useWatch({
    name: ["file"],
    control,
  });

  const submitForm = async (data: IFormData) => {
    let fileString = "";
    if (!isNullOrUndefined(data.file)) {
      fileString = (await convertFileToDataUrl(data.file)).split(",")[1] ?? "";
    }

    const response = await addRequestedServiceProvider({
      jointBillServiceId: data.jointBillServiceId,
      data: {
        ...(!isNullOrUndefined(data.requestedServiceProviderName.value)
          ? {
              requestedServiceProviderId:
                data.requestedServiceProviderName.value,
            }
          : {}),
        requestedServiceProviderName: data.requestedServiceProviderName.label,
        ...(!isNullOrUndefined(data.customerCode)
          ? {
              customerCode: data.customerCode,
            }
          : {}),
        ...(!isNullOrUndefined(data.note)
          ? {
              note: data.note,
            }
          : {}),
        ...(fileString
          ? {
              pdfBill: fileString,
            }
          : {}),
      },
    }).catch(setApiErrors);

    if (!isNullOrUndefined(response)) {
      toast.success(t("providerRequestModal.toast.success"));
      modal.close();
    }
  };

  return (
    <Modal
      modal={modal}
      badgeProps={{ icon: AddDocumentIcon }}
      actionButtons={
        <React.Fragment>
          <Button variant="secondary" onClick={modal.close} className="w-full">
            {t("providerRequestModal.actions.cancel")}
          </Button>
          <Button
            className="w-full"
            onClick={handleSubmit(submitForm)}
            isLoading={isPending}
          >
            {t("providerRequestModal.actions.submit")}
          </Button>
        </React.Fragment>
      }
      title={t("providerRequestModal.title")}
      subtitle={t("providerRequestModal.subtitle")}
    >
      <div className="flex flex-col gap-4">
        <FormSelect
          label={t("providerRequestModal.form.fields.jointBillServiceId.label")}
          name="jointBillServiceId"
          control={control}
          options={jointBillServiceIdOptions}
        />
        <FormAutocomplete
          name="requestedServiceProviderName"
          control={control}
          icon={MagnifyingGlassIcon}
          placeholder={t(
            "providerRequestModal.form.fields.requestedServiceProviderName.placeholder",
          )}
          options={serviceProviderOptions ?? []}
          disabled={!serviceProviderOptions}
          label={t(
            "providerRequestModal.form.fields.requestedServiceProviderName.label",
          )}
          isLoading={isServiceProvidersLoading}
          isCustomValueMode={true}
        />
        <FormInput
          name="customerCode"
          control={control}
          label={t("providerRequestModal.form.fields.customerCode.label")}
        />
        <FormTextarea
          name="note"
          control={control}
          label={t("providerRequestModal.form.fields.note.label")}
          placeholder={t("providerRequestModal.form.fields.note.placeholder")}
          className="h-32"
        />
        <p className="text-sm text-grey-dark">
          {t("providerRequestModal.fileUploadDescription")}
        </p>
        <FileUploadButton
          name="file"
          control={control}
          setValue={setValue}
          acceptedFiles={{ "application/pdf": [".pdf"] }}
        />
        {!isNullOrUndefined(file) && (
          <Notification
            color="green"
            icon={DocumentIcon}
            description={
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    t("providerRequestModal.chosenFile", {
                      fileName: file.name ?? "",
                    }),
                  ),
                }}
              />
            }
            button={
              <Button
                variant="link"
                onClick={() => setValue("file", undefined)}
              >
                {t("providerRequestModal.actions.removeFile")}
              </Button>
            }
          />
        )}
      </div>
    </Modal>
  );
};
